import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { PopupsManagerService } from './popups-manager.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TilesService } from './tiles.service';
import { PurchaseFlowSteps } from '../common/enums/purchaseFlow';
import { Point } from '../common/types/size';
import { StepChange } from '../common/types/purchaseFlow';
import { Events } from '../components/wall/events';
import { EventKey } from '../common/enums/event-key';
import { HttpClient } from '@angular/common/http';
import { finalize, map, tap } from 'rxjs/operators';
import { EthereumService } from './ethereum.service';
import { CartItemPayload, ComplexWallItem } from '../common/types/wall-item';
import { Router } from '@angular/router';
import { CommonService } from './common.service';

export const TileBlocks = [
  { size: { x: 1, y: 1 } },
  { size: { x: 2, y: 2 } },
  { size: { x: 3, y: 3 } },
  { size: { x: 4, y: 4 } },
];

export type Prices = { usd: number; eth: number };

@Injectable({
  providedIn: 'root',
})
export class PurchaseFlowService {
  public prices: Prices = {} as Prices;
  public nftArtFormGroup: FormGroup | undefined;
  public pillSize!: Point | null;
  private readonly TILE_PRICE: number = 0.01;
  private currentStep: PurchaseFlowSteps = PurchaseFlowSteps.Tile;
  private stepChangeSub: Subject<StepChange> = new BehaviorSubject<StepChange>({
    step: this.currentStep,
  });
  public stepChange$: Observable<StepChange> =
    this.stepChangeSub.asObservable();
  public selectedAssetId?: string | null;
  private tilesData: { mainTileId: string; tiles: string[] } | undefined;
  private addingToCartSub: Subject<boolean> = new Subject<boolean>();
  public addingToCart$ = this.addingToCartSub.asObservable();

  constructor(
    private popupsManagerService: PopupsManagerService,
    private ethereumService: EthereumService,
    private cartService: TilesService,
    private http: HttpClient,
    private fb: FormBuilder,
    private router: Router,
    private commonService: CommonService
  ) {
    this.initForms();
    Events.on(EventKey.TilesData).subscribe(
      ({ data }) => (this.tilesData = data as any)
    );
  }

  public nextStep(): void {
    switch (this.currentStep) {
      case PurchaseFlowSteps.Tile: {
        if (!this.pillSize) {
          return;
        }
        this.updateAndEmitStepValue();
        break;
      }
      case PurchaseFlowSteps.ChooseImage: {
        if (!this.selectedAssetId) {
          return;
        }
        this.saveData();
        break;
      }
      default: {
        this.updateAndEmitStepValue();
      }
    }
  }

  public editCartItem(
    wallItem: ComplexWallItem,
    onAfterEdit?: () => void
  ): void {
    const payload: CartItemPayload = {
      tiles: wallItem?.tiles.map((wi) => wi.id) as string[],
      tokenId: this.selectedAssetId as string,
    };
    this.commonService.loadingChange(true);
    this.cartService
      .editCartItem(wallItem.mainWallItemId, payload)
      .pipe(finalize(() => this.commonService.loadingChange(false)))
      .subscribe(() => {
        this.emitPopupHide();
        onAfterEdit?.();
      });
  }

  public previousStep(): void {
    if (this.currentStep === PurchaseFlowSteps.Tile) {
      this.emitPopupHide();
      return;
    }
    --this.currentStep;
    this.stepChangeSub.next({ step: this.currentStep });
  }

  public emitPopupHide(): void {
    Events.emit({ key: EventKey.HidePopup });
    this.popupsManagerService.hide();
  }

  public selectedAssetChange(assetId: string | null): void {
    this.selectedAssetId = assetId;
  }

  public getPrices(blockSize: number): Observable<Prices> {
    return of(this.ethereumService.ethereumPrice).pipe(
      tap((ethPrice) => {
        if (!ethPrice) {
          return;
        }
        this.prices.eth = Math.pow(blockSize, 2) * this.TILE_PRICE;
        this.prices.usd = this.prices.eth * ethPrice;
      }),
      map(() => this.prices)
    );
  }

  public resetValues() {
    this.nftArtFormGroup = void 0;
    this.pillSize = null;
    this.currentStep = PurchaseFlowSteps.Tile;
    this.initForms();
    this.stepChangeSub.next({ step: this.currentStep });
    this.selectedAssetChange(null);
  }

  private saveData(): void {
    this.addingToCartSub.next(true);
    const payload: CartItemPayload = {
      tiles: this.tilesData?.tiles as string[],
      tokenId: this.selectedAssetId as string,
    };
    this.cartService
      .addToCart(payload)
      .pipe(
        tap(() => this.emitPopupHide()),
        finalize(() => this.addingToCartSub.next(false))
      )
      .subscribe((res) => {
        if (res) {
          this.router.navigateByUrl('/cart');
        }
      });
  }

  private initForms(): void {
    this.nftArtFormGroup = this.fb.group({
      image: [null, [Validators.required]],
    });
    this.pillSize = TileBlocks[0].size;
  }

  private updateAndEmitStepValue(): void {
    ++this.currentStep;
    this.stepChangeSub.next({
      step: this.currentStep,
      next: true,
    });
  }
}
